import { Fragment, useEffect, useState, useRef, useMemo } from 'react';
import { Dialog, Menu, Transition } from '@headlessui/react';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import MailIcon from '@mui/icons-material/Mail';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import MenuIcon from '@mui/icons-material/Menu';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import { Fab, useMediaQuery } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';

import NavigationMenu from './NavigationMenu.js';
import usePages from '../../context/PageContext.js';
import useUsers from '../../context/UsersContext.js';
import useParam from '../../context/ParamContext.js';
import appService from '../../service/app.service.js';
import MessagesDialog from './MessagesDialog.jsx';
import { Box, IconButton, Tooltip } from '@mui/material';
import { getIcon, getInitials } from '../../utils/helper.js';
import { sxConfig } from './Layout.config';
import EventLogger from './EventLogger.jsx';
import expiryMonitoringService from '../../service/expiryMonitoring.service.js';
import useMenus from '../../context/MenuContext.js';
import { useTheme } from '@mui/material/styles';
import AlertPanelDialog from '../Alert/AlertMessage/AlertMessageDialog.jsx';
import alertMessageService from '../../service/Alert/alertMessage.service';
import { saveSystemLog } from '../../utils/saveSystemLog.js';

export default function Layout() {
  const theme = useTheme();
  const ui = theme.palette.ui;
  const location = useLocation();
  const { pages, setPages } = usePages();
  const { menus, getSubMenuNamesByUrls, getSubMenuNameByPath } = useMenus();
  const { getParam, params } = useParam();
  const { getUser, user } = useUsers();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [messagesDialog, setMessagesDialog] = useState({ open: false, count: 0, messages: [] });
  const [expiringCount, setExpiringCount] = useState(null);
  const [boxOpen, setBoxOpen] = useState(false);
  const [alertPanel, setAlertPanel] = useState({ open: false, count: 0 });

  // Draggable mobile FAB position + drag state
  const [mobileMenuPos, setMobileMenuPos] = useState({ x: 16, y: 16 });
  const dragRef = useRef({ dragging: false, offsetX: 0, offsetY: 0, moved: false, startX: 0, startY: 0 });
  const MOVE_THRESHOLD = 8; // px threshold to treat as drag on Android

  // LocalStorage helpers for persisting FAB position (replaces cookie-based storage)
  const setLocalJSON = (name, value) => {
    try {
      localStorage.setItem(name, JSON.stringify(value));
    } catch {}
  };
  const getLocalJSON = (name) => {
    try {
      const raw = localStorage.getItem(name);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const fromKanbanBoard = location?.pathname?.split('/')[2];

  const navigate = useNavigate();
  const isLgUp = useMediaQuery('(min-width:960px)');

  useEffect(() => setSidebarOpen(false), []);

  // Init mobile FAB default/saved position (mobile only)
  useEffect(() => {
    if (isLgUp) return; // only for < 1024px
    const saved = getLocalJSON('mobileMenuBtnPos'); // removed cookie migration
    if (saved) {
      setMobileMenuPos(saved);
      return;
    }
    const size = 56; // approximate FAB size
    setMobileMenuPos({
      x: Math.max(8, window.innerWidth - size - 16),
      y: Math.max(8, window.innerHeight - size - 100),
    });
  }, [isLgUp]);

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
    getParam();
    getExpiringFieldsCount();
  }, []);

  const showMessageIcon = params.SHOW_NOTIFICATION_ICON;

  const getAlert = (uId = user?.userId) => {
    if (!uId) return; // guard: ne fusson undefined userId-val
    alertMessageService.getMessageCount(uId).then((data) => {
      setAlertPanel((prev) => {
        if (params.HARD_ALERT === 'Y' && data.count > prev.count) {
          return { ...prev, open: true, count: data.count ?? 0 };
        }

        return { ...prev, count: data.count ?? 0 };
      });
    });
  };

  useEffect(() => {
    if (!user?.userId) return;

    let msgIntervalId;
    let alertIntervalId;

    if (showMessageIcon === 'Y') {
      getMessagesCount();
      msgIntervalId = setInterval(() => getMessagesCount(), 120000);
    }

    getAlert(user.userId);
    alertIntervalId = setInterval(() => getAlert(user.userId), 60000);

    return () => {
      if (msgIntervalId) clearInterval(msgIntervalId);
      if (alertIntervalId) clearInterval(alertIntervalId);
    };
  }, [user?.userId, showMessageIcon]);

  useEffect(() => {
    if (!alertPanel.open && user?.userId) {
      getAlert(user.userId); // csak akkor, ha a user már betöltődött
    }
  }, [alertPanel.open, user?.userId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (boxOpen && !event.target.closest('[data-box-content]') && !event.target.closest('[data-box-toggle]')) {
        setBoxOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [boxOpen]);

  const submenuNames = useMemo(() => {
    return getSubMenuNamesByUrls(['expiryMonitoring']);
  }, [menus]);

  const routeSubMenuName = useMemo(() => {
    return getSubMenuNameByPath(location.pathname);
  }, [getSubMenuNameByPath, location.pathname, menus]);

  const currentSubMenuName =
    pages?.pathname === location.pathname && pages?.subMenuName ? pages.subMenuName : routeSubMenuName;
  const isTaskResourcePlannerRoute = location.pathname.toLowerCase().startsWith('/app/taskresourceplanner');

  const getExpiringFieldsCount = () => {
    expiryMonitoringService.getExpiringFieldsCount().then((data) => {
      setExpiringCount(Number(data) || 0);
    });
  };

  const getMessagesCount = () => {
    appService.getMessagesCount(user.userId).then((data) => {
      setMessagesDialog((prev) => ({
        ...prev,
        count: data.count,
      }));
    });
  };

  const getMessages = () => {
    appService.getMessages(user.userId).then((data) => {
      setMessagesDialog((prev) => ({
        ...prev,
        open: true,
        messages: data,
      }));
    });
  };

  const handleReadAtAll = () => {
    appService.updateAllMsgReadAt(user.userId).then((data) => {
      setMessagesDialog((prev) => ({
        ...prev,
        messages: data,
      }));
    });
  };

  const logout = () => {
    saveSystemLog({
      operation: 'Kijelentkezés',
      createdBy: user.userId,
    });
    navigate('/');
  };

  const handleNavigate = (data) => {
    saveSystemLog({
      operation: 'Navigálás',
      description: `A(z) ${data.name} felületre lépett.`,
      createdBy: user.userId,
    });
    navigate(`/app/${data.subMenuUrl}`);
    setPages({ subMenuName: data.name });
  };

  const handleNavigateExpiringPage = () => {
    navigate(`/app/expiryMonitoring`);
    setPages({ subMenuName: submenuNames[0] });
  };

  const toggleBox = () => {
    setBoxOpen((prev) => !prev);
  };

  // Drag handlers
  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  const onPointerMove = (e) => {
    if (!dragRef.current.dragging) return;
    e.preventDefault();
    const clientX = e.clientX ?? e.touches?.[0]?.clientX;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY;
    const size = 56;
    const x = clamp(clientX - dragRef.current.offsetX, 8, window.innerWidth - size - 8);
    const y = clamp(clientY - dragRef.current.offsetY, 8, window.innerHeight - size - 8);
    setMobileMenuPos({ x, y });
    // Only consider as moved if beyond threshold (Android can send tiny moves)
    const dx = clientX - dragRef.current.startX;
    const dy = clientY - dragRef.current.startY;
    if (!dragRef.current.moved && Math.hypot(dx, dy) > MOVE_THRESHOLD) {
      dragRef.current.moved = true;
    }
  };
  const onPointerUp = () => {
    if (!dragRef.current.dragging) return;
    dragRef.current.dragging = false;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
    window.removeEventListener('pointercancel', onPointerUp);
    window.removeEventListener('touchmove', onPointerMove);
    window.removeEventListener('touchend', onPointerUp);
    window.removeEventListener('touchcancel', onPointerUp);
    // persist to localStorage on drag end
    setLocalJSON('mobileMenuBtnPos', mobileMenuPos);
    if (!dragRef.current.moved) setSidebarOpen(true); // tap without drag opens menu
    // Reset moved so click fallback is not blocked on Android
    dragRef.current.moved = false;
  };
  const onMobileMenuPointerDown = (e) => {
    if (dragRef.current.dragging) return;
    e.preventDefault();
    e.stopPropagation();
    const clientX = e.clientX ?? e.touches?.[0]?.clientX;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY;
    dragRef.current = {
      dragging: true,
      moved: false,
      offsetX: clientX - mobileMenuPos.x,
      offsetY: clientY - mobileMenuPos.y,
      startX: clientX,
      startY: clientY,
    };
    window.addEventListener('pointermove', onPointerMove, { passive: false });
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
    // Fallback for some mobile browsers
    window.addEventListener('touchmove', onPointerMove, { passive: false });
    window.addEventListener('touchend', onPointerUp);
    window.addEventListener('touchcancel', onPointerUp);
  };

  // Recalculate/clamp FAB position on viewport resize/orientation change (mobile only)
  useEffect(() => {
    if (isLgUp) return;
    const size = 56;
    const handleResize = () => {
      setMobileMenuPos((prev) => {
        const x = clamp(prev.x, 8, window.innerWidth - size - 8);
        const y = clamp(prev.y, 8, window.innerHeight - size - 8);
        const next = { x, y };
        // persist after clamp
        setLocalJSON('mobileMenuBtnPos', next);
        return next;
      });
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [isLgUp]);

  return (
    <div className="h-screen">
      <Transition.Root show={sidebarOpen} as={Fragment} style={{ opacity: ui.headerOpacity }}>
        <Dialog as="div" className="relative z-50" onClose={setSidebarOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/80" />
          </Transition.Child>
          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative flex w-full flex-1 h-screen max-w-none lg:max-w-xs lg:mr-16">
                {/* Close button moved inside, top-right */}
                <div className="absolute top-3 right-3 z-10">
                  <button type="button" className="p-2.5" onClick={() => setSidebarOpen(false)}>
                    <span className="sr-only">Menü bezárása</span>
                    <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                  </button>
                </div>
                {/* Sidebar content */}
                <Box
                  className="flex grow flex-col gap-y-5 overflow-y-auto px-6 pb-2 pt-10 lg:pt-0 ring-1 ring-white/10"
                  sx={{ background: ui.navigation }}
                >
                  <nav>
                    <div className="relative my-3 text-white cursor-pointer ml-3">
                      <NavigationMenu
                        setSidebarOpen={setSidebarOpen}
                        sidebarOpen={sidebarOpen}
                        isMobile={!isLgUp}
                        favouriteMenu={user?.favouriteMenu?.filter((f) => f.showOnNavbar)}
                        onFavNavigate={handleNavigate}
                        onLogout={logout}
                        messages={messagesDialog.messages}
                        showMessageIcon={showMessageIcon === 'Y'}
                        showExpiryCountIcon={params.SHOW_EXPIRINGCOUNT_ICON === 'Y'}
                        expiryCount={expiringCount}
                        getMessages={getMessages}
                        handleNavigateExpiringPage={handleNavigateExpiringPage}
                      />
                    </div>
                  </nav>
                </Box>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
      <Box
        className="hidden lg:flex fixed w-full top-0 z-40 h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 px-4 shadow-sm"
        sx={{ background: params.APP_IS_TEST_VERSION === 'Y' ? '#E57373' : ui.header }}
      >
        <button type="button" className="-m-2.5 p-2.5 text-gray-700" onClick={() => setSidebarOpen(true)}>
          <span className="sr-only">Menü megnyitása</span>
          <MenuIcon className="h-6 w-6" sx={{ color: ui.headerColor }} aria-hidden="true" />
        </button>
        <Box className="h-6 w-px" sx={{ background: ui.headerColor }} aria-hidden="true" />
        {!isTaskResourcePlannerRoute && (
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
            <Box className="text-2xl" sx={{ color: ui.headerColor }}>
              {currentSubMenuName}
            </Box>
          </Breadcrumbs>
        )}
        {params.APP_IS_TEST_VERSION === 'Y' && <div className="text-center w-full text-5xl">Teszt verzió</div>}
        <div className="absolute inset-0 left-16 right-16 flex items-center justify-center gap-x-4 overflow-auto"></div>
        <div className="absolute right-4 flex items-center gap-10">
          <Box display="flex" gap="0.8rem" mr={fromKanbanBoard === 'taskkanban' ? `60px ` : 0}>
            {user?.favouriteMenu
              ?.filter((favouriteMenu) => favouriteMenu.showOnNavbar)
              ?.map((favouriteMenu) => (
                <IconButton
                  sx={{ margin: 0, padding: 0 }}
                  key={favouriteMenu.id}
                  onClick={() => handleNavigate(favouriteMenu)}
                >
                  <Tooltip title={favouriteMenu.name} placement="top">
                    {getIcon(favouriteMenu.icon, '1.7rem', ui.headerColor)}
                  </Tooltip>
                </IconButton>
              ))}
          </Box>
          <Box display="flex" gap="1rem">
            {showMessageIcon && showMessageIcon === 'Y' && (
              <Badge badgeContent={messagesDialog.count} sx={{ color: 'white' }}>
                <MailIcon
                  color="action"
                  className="cursor-pointer"
                  sx={{ color: ui.headerColor }}
                  onClick={() => getMessages()}
                />
              </Badge>
            )}

            {params.SHOW_EXPIRINGCOUNT_ICON === 'Y' && (
              <Badge
                badgeContent={expiringCount}
                sx={{ color: 'white', cursor: 'pointer' }}
                onClick={() => handleNavigateExpiringPage()}
              >
                {getIcon('PriorityHigh', '1.5rem', '#E3242B')}
              </Badge>
            )}

            <Badge badgeContent={alertPanel.count} color="error" sx={{ color: 'white' }} max={99}>
              <NotificationImportantIcon
                color="action"
                className="cursor-pointer"
                sx={{ color: ui.headerColor }}
                onClick={() => setAlertPanel((prev) => ({ ...prev, open: true }))}
              />
            </Badge>
          </Box>

          {/* @TODO kivenni */}
          <Menu as="div" className="relative">
            <Menu.Button className="-m-1.5 flex items-center p-1.5">
              <span className="sr-only">Profil menü megnyitása</span>
              <Avatar sx={{ bgcolor: '#F9BC04' }}>{getInitials(user?.nickName)}</Avatar>
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 z-10 mt-2.5 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                <Menu.Item>
                  <button onClick={logout} className="block px-3 py-1 text-sm leading-6 text-gray-900">
                    Kijelentkezés
                  </button>
                </Menu.Item>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </Box>
      <div className="hidden lg:block h-[64px]"></div>
      <Outlet />
      <MessagesDialog
        open={messagesDialog.open}
        handleClose={() => setMessagesDialog((prev) => ({ ...prev, open: false }))}
        messages={messagesDialog.messages}
        handleReadAtAll={handleReadAtAll}
      />

      <AlertPanelDialog
        open={alertPanel.open}
        onClose={() => {
          setAlertPanel((prev) => ({ ...prev, open: false }));
        }}
      />
      {boxOpen && (
        <Box sx={sxConfig.box} data-box-content>
          <EventLogger />
        </Box>
      )}
      {params.EVENTLOG_ENABLED === 'Y' && (
        <Fab
          color={boxOpen ? 'secondary' : 'primary'}
          aria-label="history"
          onClick={toggleBox}
          sx={sxConfig.fab}
          data-box-toggle
        >
          <HistoryIcon />
        </Fab>
      )}
      {/* Mobile floating, draggable menu button */}
      {!isLgUp && !sidebarOpen && (
        <Fab
          color="primary"
          aria-label="menu"
          sx={{ position: 'fixed', zIndex: 2000, touchAction: 'none' }}
          style={{ left: mobileMenuPos.x, top: mobileMenuPos.y }}
          onPointerDown={onMobileMenuPointerDown}
          onTouchStart={onMobileMenuPointerDown}
          onMouseDown={onMobileMenuPointerDown}
          onClick={(e) => {
            // Fallback open for browsers without Pointer Events
            e.preventDefault();
            e.stopPropagation();
            if (dragRef.current.dragging || dragRef.current.moved) return;
            setSidebarOpen(true);
          }}
        >
          <Bars3Icon className="h-6 w-6 text-white" />
        </Fab>
      )}
    </div>
  );
}
